import type { Game } from "@chessalpha/core";
import { gameSchema, z } from "@ssf/play-common";
import {
  equalTo,
  gamesRef,
  onValue,
  orderByChild,
  query,
  ref,
} from "@ssf/play-components";
import { useEffect, useState } from "react";

const gamesSchema = z.record(gameSchema);

export const useTeamGames = (room: string, memberIds: number[]) => {
  const memberIdsKey = [...new Set(memberIds)].sort((a, b) => a - b).join(",");
  const queryKey = `${room}:${memberIdsKey}`;
  const [state, setState] = useState<{
    queryKey: string;
    games: [string, Game][];
  }>();

  useEffect(() => {
    const memberUids = memberIdsKey
      ? memberIdsKey.split(",").map((memberId) => `memdb_${memberId}`)
      : [];
    if (!memberUids.length) {
      setState({ queryKey, games: [] });
      return;
    }

    let active = true;
    const gamesByQuery = new Map<string, Record<string, Game>>();
    const loadedQueries = new Set<string>();
    const expectedQueries = memberUids.length * 2;

    const update = (key: string, games: Record<string, Game>) => {
      gamesByQuery.set(key, games);
      loadedQueries.add(key);
      if (!active || loadedQueries.size < expectedQueries) return;

      const teamGames = new Map<string, Game>();
      for (const queryGames of gamesByQuery.values()) {
        for (const [gid, game] of Object.entries(queryGames)) {
          teamGames.set(gid, game);
        }
      }
      setState({
        queryKey,
        games: [...teamGames].sort(([, a], [, b]) => b.createdAt - a.createdAt),
      });
    };

    const unsubscribes = memberUids.flatMap((uid) =>
      (["white", "black"] as const).map((color) => {
        const key = `${color}:${uid}`;
        return onValue(
          query(
            ref(gamesRef, room),
            orderByChild(`${color}/uid`),
            equalTo(uid),
          ),
          (snapshot) => {
            const parsed = gamesSchema.safeParse(snapshot.val() ?? {});
            if (!parsed.success) {
              console.error("Failed to parse team games", parsed.error);
            }
            update(key, parsed.success ? parsed.data : {});
          },
          (error) => {
            console.error("Failed to load team games", error);
            update(key, {});
          },
        );
      }),
    );

    return () => {
      active = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [memberIdsKey, queryKey, room]);

  return state?.queryKey === queryKey ? state.games : undefined;
};
